import { FunctionComponent, useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { BulkAddStepProps } from "../BulkAddDialogTypes";
import { getHref } from "../../../utils/getHref";
import LinkIcon from "@mui/icons-material/Link";
import { api } from "../../api";
import { useApiMutation } from "../../hooks/useApiMutation";
import { makeUseServiceCall } from "../../hooks/useServiceCall";

export const BackupDataStep: FunctionComponent<BulkAddStepProps> = ({
  onNext,
  organizationId,
  orgSubdomain,
}) => {
  const [exportStatus, setExportStatus] = useState<
    null | "success" | "error" | "processing"
  >(null);
  const [exportInitiated, setExportInitiated] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [shouldRefresh, setShouldRefresh] = useState(true);

  // Fetch recent exports with auto-refresh every 5 seconds
  const { data: recentExportsResponse } = makeUseServiceCall(
    api.myExports.list,
    !!organizationId,
    shouldRefresh ? 5000 : undefined
  )({
    targetEntity: "organization",
    targetId: organizationId,
    exportEntity: "assets",
    status: "$in,processing,completed",
    limit: 3, // Get recent exports
  });

  // Countdown timer for refresh indicator
  useEffect(() => {
    if (!shouldRefresh) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return 5; // Reset to 5 seconds
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [shouldRefresh]);

  // Stop auto-refresh when no exports are processing
  useEffect(() => {
    if (recentExportsResponse?.data?.data) {
      const hasProcessingExports = recentExportsResponse.data.data.some(
        (exportItem: any) => exportItem.status === "processing"
      );
      setShouldRefresh(hasProcessingExports);
      if (!hasProcessingExports) {
        setExportInitiated(false); // can export again now
        setCountdown(5); // Reset countdown when stopping refresh
      }
    }
  }, [recentExportsResponse]);

  // Check if user has initiated an export all assets in this org within the last 3 hours
  // filtering is limited for export api but n is small enough here that
  // we can just filter in the client
  useEffect(() => {
    if (recentExportsResponse?.data?.data && !exportInitiated) {
      if (recentExportsResponse.data.data.length === 0) {
        return;
      }
      const threeHoursAgo = Date.now() - 3 * 60 * 60 * 1000; // 3 hours in milliseconds

      const recentExport = recentExportsResponse.data.data.find(
        (exportItem: any) => {
          const exportCreatedTime = new Date(exportItem.created_date).getTime();
          return (
            exportCreatedTime >= threeHoursAgo &&
            exportItem.export_entity === "assets" &&
            exportItem.target_id === organizationId
          );
        }
      );

      if (recentExport) {
        setExportStatus("success");
      }
    }
  }, [recentExportsResponse, exportInitiated, organizationId]);

  // Helper function to get status color and label
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "processing":
        return { color: "info", label: "Processing" };
      case "completed":
        return { color: "success", label: "Completed" };
      case "errored":
        return { color: "error", label: "Error" };
      default:
        return { color: "default", label: status };
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleGoToEnhancedAssets = () => {
    window.location.assign(`${getHref(orgSubdomain)}/enhanced_assets`);
  };

  const { trigger: createExportJob } = useApiMutation(api.myExports.create);

  const handleExportRequest = async () => {
    setExportStatus(null);
    setShouldRefresh(true);
    try {
      await createExportJob({
        args: [
          {
            myExportsCreateRequest: {
              target_entity: "organization",
              target_id: organizationId,
              export_entity: "assets",
              columns: [],
              filters: "",
            },
          },
        ],
      });
      setExportInitiated(true);
      setExportStatus("success");
    } catch (e) {
      setExportStatus("error");
    }
  };

  return (
    <Box css={(theme) => ({ padding: theme.spacing(2) })}>
      <Typography fontWeight="bold" gutterBottom>
        Step 1: Back up your data
      </Typography>
      <Typography gutterBottom>
        Click here to create an export of your assets before proceeding. You can
        use this to revert changes if needed. We recommend you verify the backup
        is complete before continuing.
      </Typography>

      {/* Recent Exports Table */}
      <Box mb={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">Recent Asset Exports</Typography>
          {shouldRefresh && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Refreshing in {countdown}s
              </Typography>
              <Box sx={{ width: 40 }}>
                <LinearProgress
                  variant="determinate"
                  value={((5 - countdown) / 5) * 100}
                  sx={{ height: 4 }}
                />
              </Box>
            </Box>
          )}
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentExportsResponse?.data?.data &&
              recentExportsResponse.data.data.length > 0 ? (
                recentExportsResponse.data.data.map((exportItem: any) => {
                  const statusDisplay = getStatusDisplay(exportItem.status);
                  return (
                    <TableRow key={exportItem.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(exportItem.created_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusDisplay.label}
                          color={statusDisplay.color as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {exportItem.filters
                            ? "Filtered Assets"
                            : "All Assets"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No recent exports found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {organizationId && (
            <>
              <Button
                color="primary"
                variant="contained"
                onClick={handleExportRequest}
                disabled={exportInitiated}
              >
                Back Up All Assets
              </Button>
              {exportInitiated && exportStatus !== "error" && (
                <Typography color="success" variant="body2" sx={{ ml: 1 }}>
                  Your asset export has been initiated.
                </Typography>
              )}
              {exportStatus === "error" && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <CancelIcon color="error" data-testid="export-error" />
                  <Typography color="error" variant="body2">
                    Something went wrong
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {orgSubdomain && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={handleGoToEnhancedAssets}
            >
              Go to Enhanced Assets
            </Button>
          )}
          <Tooltip
            title={
              exportStatus !== "success"
                ? "Back up asset data before continuing"
                : ""
            }
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  zIndex: (theme) => theme.zIndex.modal + 1000,
                },
              },
              popper: {
                sx: {
                  zIndex: (theme) => theme.zIndex.modal + 1000,
                },
              },
            }}
          >
            <span>
              <Button
                variant="outlined"
                onClick={onNext}
                disabled={exportStatus !== "success"}
              >
                Continue
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
